import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Calendar, ArrowRight } from "lucide-react";
import type { MagazineTitle, MagazineIssue } from "@shared/schema";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";

interface MagazineWithIssues extends MagazineTitle {
  issues: MagazineIssue[];
}

export default function MagazineListingPage() {
  const { data: magazines, isLoading } = useQuery<MagazineTitle[]>({
    queryKey: ["/api/magazines"],
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="container mx-auto px-4 pt-4">
        <PageBreadcrumb items={[{ label: "Magazine" }]} />
      </div>
      
      <main className="flex-1">
        <div className="bg-gradient-to-b from-primary/10 to-background py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold" data-testid="text-page-title">Magazines</h1>
            </div>
            <p className="text-muted-foreground max-w-2xl">
              Explore our digital magazine collection with in-depth articles, industry insights, and expert analysis.
            </p>
          </div>
        </div>
        
        <div className="container mx-auto px-4 py-8">
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-32 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : magazines && magazines.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {magazines.map((magazine) => (
                <MagazineCard key={magazine.id} magazine={magazine} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">No magazines available</h2>
              <p className="text-muted-foreground">Check back soon for new publications.</p>
            </div>
          )}
        </div>
      </main>
      
      <SiteFooter />
    </div>
  );
}

function MagazineCard({ magazine }: { magazine: MagazineTitle }) {
  const { data: magazineData } = useQuery<MagazineWithIssues>({
    queryKey: ["/api/magazines", magazine.slug],
  });

  const latestIssue = magazineData?.issues?.[0];

  return (
    <Card className="hover-elevate transition-all" data-testid={`card-magazine-${magazine.slug}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-lg">{magazine.name}</CardTitle>
            {magazine.tagline && (
              <CardDescription>{magazine.tagline}</CardDescription>
            )}
          </div>
          {magazine.logo && (
            <img 
              src={magazine.logo} 
              alt={magazine.name} 
              className="w-10 h-10 object-contain"
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {magazine.description && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {magazine.description}
          </p>
        )}
        
        {latestIssue ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary">
                <Calendar className="w-3 h-3 mr-1" />
                Issue {latestIssue.issueNumber}
              </Badge>
              {latestIssue.name && (
                <span className="text-muted-foreground">{latestIssue.name}</span>
              )}
            </div>
            
            <Link href={`/magazine/${magazine.slug}/${latestIssue.issueNumber}`}>
              <Button className="w-full" data-testid={`button-read-${magazine.slug}`}>
                Read Latest Issue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No published issues yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}
